/* eslint-disable no-mixed-spaces-and-tabs */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getComparator, stableSort } from '@/components/table/functions';
import { getAggregateFromServer, sum } from 'firebase/firestore';
import BASE_QUERY from '@/features/baseQuery';
import { FILTER_TYPES } from './data';

// Creating an API service using RTK Query
export const policiesApi = createApi({
	reducerPath: 'policiesApi',
	tagTypes: ['POLICIES', 'POLICY_TRANSACTIONS'],
	baseQuery: fetchBaseQuery(BASE_QUERY),
	endpoints: (builder) => ({
		searchInsightPolicyTransactions: builder.infiniteQuery({
			providesTags: ['POLICY_TRANSACTIONS'],
			infiniteQueryOptions: {
				initialPageParam: {
					PageNumber: 1,
					RowsPerPage: 100,
				},
				getNextPageParam: (lastPage, allPages, lastPageParam, allPageParams) => {
					const nextPage = lastPageParam.PageNumber + 1;
					const totalPages = lastPage?.PageCount ?? 0;

					if (nextPage > totalPages) return undefined;

					return { ...lastPageParam, PageNumber: nextPage };
				},
				getPreviousPageParam: (firstPage, allPages, firstPageParam, allPageParams) => {
					const prevPage = firstPageParam.PageNumber - 1;
					if (prevPage <= 0) return undefined;
					return firstPageParam;
				},
			},
			queryFn: async (
				{ pageParam, queryArg },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				const { PageNumber } = pageParam;
				const {
					filterBy,
					searchText,
					expiryFromDate,
					expiryToDate,
					authRepId,
					clientId,
					ownerDocIds = [],
					fundingStatus,
					effectiveToDate,
					effectiveFromDate,
					displayOnlyPosted,
					policyId,
					// useTestData,
				} = queryArg;

				const params = {
					PageNumber: PageNumber,
					RowsPerPage: 500,
					...(searchText && { searchText }),
					...(clientId && { clientId }),
					authRepId,
					filterBy: filterBy ?? null,
					...(expiryFromDate && { expiryFromDate }),
					...(expiryToDate && { expiryToDate }),
					...(ownerDocIds.length > 0 && { ownerDocIds }),
					...(fundingStatus && { fundingStatus }),
					...(effectiveToDate && { effectiveToDate }),
					...(effectiveFromDate && { effectiveFromDate }),
					...(displayOnlyPosted && { displayOnlyPosted }),
					...(policyId && { policyId }),
				};

				const response = await baseQuery({
					// url: ,
					//http://localhost:3001/policyTransactions/search
					url: process.env.REACT_APP_SEARCH_INSIGHT_POLICY_TRANSACTIONS,
					method: 'POST',
					body: params,
				});
				console.log('[searchInsightPolicyTransactions] Response', response.data);
				if (response.error) {
					console.error(
						'[searchInsightPolicyTransactions] Error retrieving policy transactions from Insight',
						response.error,
					);
					return {
						error: {
							code: response.error?.status,
							message: response.error?.data?.message,
						},
					};
				}
				return {
					data: response.data,
				};
			},
		}),
		getInsightPolicies: builder.infiniteQuery({
			providesTags: ['POLICIES'],
			infiniteQueryOptions: {
				initialPageParam: {
					PageNumber: 1,
					RowsPerPage: 100,
				},
				getNextPageParam: (lastPage, allPages, lastPageParam) => {
					const nextPage = lastPageParam.PageNumber + 1;
					const totalPages = lastPage?.PageCount ?? 0;

					if (nextPage > totalPages) return undefined;

					return { ...lastPageParam, PageNumber: nextPage };
				},
				getPreviousPageParam: (firstPage, allPages, firstPageParam, allPageParams) => {
					const prevPage = firstPageParam.PageNumber - 1;
					if (prevPage <= 0) return undefined;
					return firstPageParam;
				},
			},
			queryFn: async (
				{ pageParam, queryArg },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				const { PageNumber } = pageParam;
				const {
					fromDate,
					toDate,
					branchIds,
					clientId,
					ownerDocIds = [],
					statusId,
					typeId,
				} = queryArg;

				const params = {
					PageNumber: PageNumber,
					RowsPerPage: 500,
					...(clientId && { clientId }),
					branchIds,
					...(fromDate && { toDateFrom: fromDate }),
					...(toDate && { toDateTo: toDate }),
					...(ownerDocIds.length > 0 && { ownerDocIds }),
					...(statusId != null && { statusId }),
					...(typeId != null && { typeId }),
				};

				const response = await baseQuery({
					url: process.env.REACT_APP_SEARCH_INSIGHT_POLICIES,
					method: 'POST',
					body: params,
				});

				console.log('[getInsightPolicies] Response', response.data);
				if (response.error) {
					console.error(
						'[getInsightPolicies] Error retrieving policies from Insight',
						response.error,
					);
					return {
						error: {
							code: response.error?.status,
							message: response.error?.data?.message,
						},
					};
				}
				return {
					data: response.data,
				};
			},
		}),
		getInsightPolicy: builder.query({
			// providesTags: (result, error, { dealId }) => {
			// 	return [{ type: 'DEAL', id: dealId }];
			// },
			query: (policyId) => ({
				url: process.env.REACT_APP_GET_INSIGHT_POLICY,
				method: 'POST',
				body: { policyId },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform getInsightPolicy error response', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform getInsightPolicy response', response);
				return response?.data?.Policy;
			},
		}),
	}),
});
//

// Exporting auto-generated hooks for the defined endpoints
export const {
	useSearchInsightPolicyTransactionsInfiniteQuery,
	useGetInsightPoliciesInfiniteQuery,
	useGetInsightPolicyQuery,
} = policiesApi;
